// ChatManager — pipeline: addChatJob (RTK mutation) → subscribe to chatJobStatusUpdatedByUuid (Apollo) → resolve on terminal state

import { isMobile } from 'react-device-detect'
import { store } from '../../api/store'
import { graphqlApi } from '../../api/graphqlApi'
import { chatJobStatusUpdatedByUuid } from '../../api/graphqlSubscription'
import { safeParseJson } from '../Utilities'

// ─── State ─────────────────────────────────────────────────────────────────

let _mode = 'backend'  // 'backend' | 'mock'
let _lastUsageDetails = null // populated from each finished turn's `usage` field
let _language = 'de'
let _sessionId = ''
let _apolloClient = null // injected by ChatWidget via setApolloClient (Apollo client lives in React context)
let _currentTurnUuid = null // uuid of the in-flight turn — used by cancelCurrentTurn

const TURN_TIMEOUT_MS = 90000 // safety net per design doc — covers missed terminal events on subscription drops

// Dashboard actions registry (set by DashboardContent, read by ChatWidget)
let _dashboardActions = null
export function setDashboardActions(actions) { _dashboardActions = actions }
export function getDashboardActions() { return _dashboardActions }

// Archive actions registry (set by ArchiveControls, read by _processActions)
let _archiveActions = null
export function setArchiveActions(actions) { _archiveActions = actions }
export function getArchiveActions() { return _archiveActions }

// MYSITE section registry — exposes expandSection(panel) + getCurrentSubpage()
let _mysiteActions = null
export function setMysiteActions(actions) { _mysiteActions = actions }

// Per-mysite-subsection registries — each exposes at least getSelectedCameraId()
let _reportActions = null
export function setReportActions(actions) { _reportActions = actions }

let _diaryActions = null
export function setDiaryActions(actions) { _diaryActions = actions }

let _downloadActions = null
export function setDownloadActions(actions) { _downloadActions = actions }

let _timelapseActions = null
export function setTimelapseActions(actions) { _timelapseActions = actions }

// Access registry — set by AccessAccordion (mysite-access subpage only). Standalone /access route uses AccessContent which doesn't register, so bot-driven access actions there navigate to /mysite first.
let _accessActions = null
export function setAccessActions(actions) { _accessActions = actions }

// Module-level getters — hoisted out of the _processActions loop body to satisfy eslint's no-loop-func
const _getReportActions = () => _reportActions
const _getDiaryActions = () => _diaryActions
const _getDownloadActions = () => _downloadActions
const _getTimelapseActions = () => _timelapseActions
const _getAccessActions = () => _accessActions

// Dashboard activation (set by Content.js when on MYSITE)
let _activateDashboard = null
export function setActivateDashboard(fn) { _activateDashboard = fn }

// Navigation registry (set by ChatWidget, provides react-router navigate)
let _navigate = null
export function setNavigate(fn) { _navigate = fn }

// Apollo client registry — set by ChatWidget via useApolloClient()/useEffect
export function setApolloClient(client) { _apolloClient = client }

// Navigate to dashboard: goes to MYSITE and activates the dashboard view
export async function navigateToDashboard() {
    const conf = window.conf || {}
    const mysitePath = conf.ROUTES?.MYSITE?.path
    if (!mysitePath || !_navigate) return false
    _navigate(mysitePath)
    const ok = await _poll(() => _activateDashboard, 100, 5000)
    if (ok) { _activateDashboard(); return true }
    return false
}

// ─── Init ──────────────────────────────────────────────────────────────────

// Initialize the chatbot system. Identity (userName/userMail/userRoles) is server-derived from the JWT in addChatJob — only config.language is used. ChatWidget still passes username/capabilities for backwards compatibility (ignored here).
export async function init(config = {}) {
    _mode = 'backend'
    _language = config.language || 'de'
    _sessionId = _makeSessionId()
}

// Plain UUID v4 — reused across turns of the same conversation
function _makeSessionId() {
    return (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : _fallbackUuid()
}

// RFC4122-ish v4 fallback for environments without crypto.randomUUID
function _fallbackUuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : ((r & 0x3) | 0x8)
        return v.toString(16)
    })
}

// ─── Mode ──────────────────────────────────────────────────────────────────

export function getMode() { return _mode }
export function isMock() { return _mode === 'mock' }

// Returns the most recent usage snapshot (populated from each finished turn's `usage` field)
export function getUsageDetail() {
    return _lastUsageDetails
}

// Switch chat mode (backend or mock for development).
export function setMode(mode) {
    if (['mock', 'backend'].includes(mode)) {
        _mode = mode
    }
}

// ─── Subpage / camera resolution ──────────────────────────────────────────

function _safeCall(fn) {
    try { return typeof fn === 'function' ? fn() : null }
    catch (e) { return null }
}

// Active subpage — 'mysite' asks the registry (null when no accordion open); other pages return their own name
function _getCurrentSubpage(currentPage) {
    if (currentPage === 'mysite') return _mysiteActions?.getCurrentSubpage?.() || null
    if (['home', 'live', 'timelapse', 'archive', 'access'].includes(currentPage)) return currentPage
    return null
}

// Per-subpage state snapshot for the payload — null for overview pages or unmounted registries
function _buildDetailsState(currentPage, currentSubpage) {
    if (currentSubpage === null) return null
    if (['home', 'live'].includes(currentSubpage)) return null  // grid views only, no controls - no per-subpage state
    if (currentSubpage === 'archive') return _archiveActions ? _captureArchive() : null
    if (currentSubpage === 'dashboard') return _dashboardActions ? _captureDashboard() : null
    if (currentSubpage === 'report') return _reportActions ? _captureReport() : null
    if (currentSubpage === 'diary') return _diaryActions ? _captureDiary() : null
    if (currentSubpage === 'download') return _downloadActions ? _captureDownload() : null
    if (currentSubpage === 'timelapse') return _timelapseActions ? _captureTimelapse() : null
    if (currentSubpage === 'access') return _accessActions ? _captureAccess() : null
    return null
}

function _captureArchive() {
    const a = _archiveActions
    return {
        date: _safeCall(a.getDate),                          // ISO 'YYYY-MM-DD HH:mm' — full moment of selected image
        min_date: _safeCall(a.getMinDate),                   // ISO 'YYYY-MM-DD' — first available image
        max_date: _safeCall(a.getMaxDate),                   // ISO 'YYYY-MM-DD' — latest available image
        can_step_left: _safeCall(a.getCanStepLeft),
        can_step_right: _safeCall(a.getCanStepRight),
    }
}

function _captureDashboard() {
    const a = _dashboardActions
    return {
        selected_camera_id: _safeCall(a.getSelectedCameraId),
        active_tab: _safeCall(a.getActiveTab),
        date_range: _safeCall(a.getDateRange),
        detection_types_enabled: _safeCall(a.getEnabledDetectionTypes),
        detection_types_available: _safeCall(a.getDetectionTypes),
        weather_streams: _safeCall(a.getWeatherStreams),
        data_loaded: _safeCall(a.getDataLoaded),
        viewer_open: _safeCall(a.getViewerDate) !== null,
        viewer_date: _safeCall(a.getViewerDate),
    }
}

function _captureReport() {
    const a = _reportActions
    return {
        selected_camera_id: _safeCall(a.getSelectedCameraId),
        available_camera_ids: _safeCall(a.getAvailableCameraIds),
        file_exists: _safeCall(a.getFileExists),
    }
}

function _captureDiary() {
    const a = _diaryActions
    return {
        selected_camera_id: _safeCall(a.getSelectedCameraId),
        available_camera_ids: _safeCall(a.getAvailableCameraIds),
        date: _safeCall(a.getDate),                          // ISO 'YYYY-MM-DD'
        file_exists: _safeCall(a.getFileExists),
    }
}

function _captureDownload() {
    const a = _downloadActions
    return {
        mode: _safeCall(a.getMode),
        selected_camera_id: _safeCall(a.getSelectedCameraId),
        date_range: _safeCall(a.getDateRange),
        predefined_range: _safeCall(a.getPredefinedRange),
        start_date_valid: _safeCall(a.getStartDateValid),
        end_date_valid: _safeCall(a.getEndDateValid),
        alert_message: _safeCall(a.getAlertMessage),
    }
}

function _captureTimelapse() {
    const a = _timelapseActions
    return {
        mode: _safeCall(a.getMode),
        selected_camera_id: _safeCall(a.getSelectedCameraId),
        date_range: _safeCall(a.getDateRange),
        predefined_range: _safeCall(a.getPredefinedRange),
        start_date_valid: _safeCall(a.getStartDateValid),
        end_date_valid: _safeCall(a.getEndDateValid),
        alert_message: _safeCall(a.getAlertMessage),
        use_recommended_length: _safeCall(a.getUseRecommendedLength),
        max_length_sec: _safeCall(a.getMaxLengthSec),
        selected_resolution: _safeCall(a.getSelectedResolution),
        available_resolutions: _safeCall(a.getAvailableResolutions),
    }
}

function _captureAccess() {
    const a = _accessActions
    return {
        selected_camera_id: _safeCall(a.getSelectedCameraId),
        available_camera_ids: _safeCall(a.getAvailableCameraIds),
        date: _safeCall(a.getDate),                          // ISO 'YYYY-MM-DD' — access UI is day-precise (no time picker)
        min_date: _safeCall(a.getMinDate),
        max_date: _safeCall(a.getMaxDate),
        can_step_left: _safeCall(a.getCanStepLeft),
        can_step_right: _safeCall(a.getCanStepRight),
        vehicle_filter: _safeCall(a.getVehicleFilter),
    }
}

// 0-based slot into window.conf.CAMERAS for the camera in focus — null when the active page is an overview
function _getActiveCameraSlot() {
    const page = _getCurrentPage()
    const sub = _getCurrentSubpage(page)
    if (sub === 'dashboard' && _dashboardActions?.getSelectedCameraId) return _safeCall(_dashboardActions.getSelectedCameraId)
    if (sub === 'report'    && _reportActions?.getSelectedCameraId)    return _safeCall(_reportActions.getSelectedCameraId)
    if (sub === 'diary'     && _diaryActions?.getSelectedCameraId)     return _safeCall(_diaryActions.getSelectedCameraId)
    if (sub === 'download'  && _downloadActions?.getSelectedCameraId)  return _safeCall(_downloadActions.getSelectedCameraId)
    // 'timelapse' only resolves on mysite-timelapse (registry is mounted there); standalone /timelapse → null
    if (sub === 'timelapse' && _timelapseActions?.getSelectedCameraId) return _safeCall(_timelapseActions.getSelectedCameraId)
    // 'access' as either a top-level page or a mysite subpage — same Redux camera selector
    if ((page === 'access' || sub === 'access') && _accessActions?.getSelectedCameraId) return _safeCall(_accessActions.getSelectedCameraId)
    return null
}

// {id, featureDetailId} for the in-focus camera, or null on multi-cam overviews. Assumes id == array index on multi-cam.
function _getSelectedCamera() {
    const cams = (window.conf && Array.isArray(window.conf.CAMERAS)) ? window.conf.CAMERAS : []
    if (!cams.length) return null
    if (cams.length === 1) {
        const cam = cams[0]
        return {
            id: typeof cam.id === 'number' ? cam.id : 0,
            featureDetailId: cam.featureDetailId != null ? String(cam.featureDetailId) : '',
        }
    }
    const slot = _getActiveCameraSlot()
    if (slot === null || slot === undefined) return null
    const cam = cams[slot]
    if (!cam) return null
    return {
        id: typeof cam.id === 'number' ? cam.id : 0,
        featureDetailId: cam.featureDetailId != null ? String(cam.featureDetailId) : '',
    }
}

// ─── Backend mode handler ──────────────────────────────────────────────────

// Drive one chat turn: addChatJob (RTK mutation, server-generated uuid) → subscribe to chatJobStatusUpdatedByUuid (Apollo Observable, imperative since we're outside React) → resolve on terminal state (finished/failed/cancelled) or 90 s safety-net timeout. Identity, capabilities, and sibling cameras are server-resolved from JWT + projectId — not in the input.
async function _handleBackend(userMessage, options) {
    if (!_apolloClient) throw new Error('Apollo client not registered — chatbot subscription unavailable')

    const language = options.language || _language
    const currentPage = options.currentPage || _getCurrentPage()
    const currentSubpage = _getCurrentSubpage(currentPage)
    const detailsState = _buildDetailsState(currentPage, currentSubpage)
    const selectedCam = _getSelectedCamera()

    // 1. enqueue (transformResponse already unwrapped the `chatMutation` namespace level)
    const result = await store.dispatch(
        graphqlApi.endpoints.addChatJob.initiate({
            sessionId: _sessionId,
            message: userMessage,
            currentPage,
            currentSubpage,
            deviceType: isMobile ? 'mobile' : 'desktop',
            detailsState: detailsState ? JSON.stringify(detailsState) : null,
            selectedCamera: selectedCam ? _toIntOrNull(selectedCam.featureDetailId) : null,
            projectId: _getProjectAnchor(),
            language,
        })
    )
    if (result.error) throw new Error(result.error.message || 'addChatJob failed')
    const uuid = result.data?.uuid
    if (!uuid) throw new Error('addChatJob did not return a uuid')
    _currentTurnUuid = uuid

    // 2. subscribe + wait for terminal state
    return new Promise((resolve, reject) => {
        let timeoutId = null
        let sub = null

        const cleanup = () => {
            if (timeoutId) clearTimeout(timeoutId)
            if (sub) sub.unsubscribe()
            if (_currentTurnUuid === uuid) _currentTurnUuid = null
        }

        timeoutId = setTimeout(() => {
            cleanup()
            resolve({ reply: null, mock: false, debug: { timedOut: true } }) // soft-resolve so ChatWidget can render a neutral retry message; row gets cleaned up server-side
        }, TURN_TIMEOUT_MS)

        sub = _apolloClient.subscribe({
            query: chatJobStatusUpdatedByUuid,
            variables: { uuid },
        }).subscribe({
            next: async ({ data }) => {
                const row = data?.chatJobStatusUpdatedByUuid
                const state = row?.jobParameter?.state
                if (state === 'finished') {
                    cleanup()
                    const actions = safeParseJson(row.actions, [])
                    const debug = safeParseJson(row.debug, null)
                    const usage = safeParseJson(row.usage, null)
                    if (usage) _lastUsageDetails = usage
                    const { dashboardActions } = await _processActions(Array.isArray(actions) ? actions : [])
                    const out = { reply: row.reply, mock: false, debug }
                    if (dashboardActions.length) out.actions = dashboardActions
                    resolve(out)
                } else if (state === 'failed') {
                    cleanup()
                    resolve({ reply: null, mock: false, debug: { error: row.error || 'failed' } })
                } else if (state === 'cancelled') {
                    cleanup()
                    resolve({ reply: null, mock: false, debug: { cancelled: true } })
                }
                // queued / processing / cancel → keep waiting; 'cancel' is the transient signal, terminal is 'cancelled'
            },
            error: (err) => { cleanup(); reject(err) },
        })
    })
}

// Cancel the in-flight turn. Fires cancelChatGeneration(uuid); backend transitions row cancel→cancelled, the open _handleBackend subscription observes and resolves the turn with debug.cancelled=true.
export async function cancelCurrentTurn() {
    if (!_currentTurnUuid) return false
    try {
        const result = await store.dispatch(
            graphqlApi.endpoints.cancelChatGeneration.initiate(_currentTurnUuid)
        )
        return !result.error
    } catch (e) {
        return false
    }
}

// Coerce a string/number featureDetailId to int, or null. window.conf.CAMERAS stores ids as strings today.
function _toIntOrNull(v) {
    if (v == null || v === '') return null
    const n = parseInt(v, 10)
    return Number.isFinite(n) ? n : null
}

// Anchor feature-detail-id for backend-side portal/capability/sibling-camera lookup. Any FeatureDetail in the project's Order works — we pick CAMERAS[0] for stability across turns.
function _getProjectAnchor() {
    const cams = (window.conf && Array.isArray(window.conf.CAMERAS)) ? window.conf.CAMERAS : []
    return cams.length ? _toIntOrNull(cams[0].featureDetailId) : null
}

// ─── Send Message ──────────────────────────────────────────────────────────

// Process a user message — routes to mock or GraphQL pipeline. Returns { reply, actions?, mock?, debug? }. Options: { language?, currentPage? } (currentPage overrides URL detection).
export async function sendMessage(userMessage, options = {}) {
    if (_mode === 'mock') {
        return _handleMock(userMessage)
    }
    return _handleBackend(userMessage, options)
}

// ─── Reset ─────────────────────────────────────────────────────────────────

export function resetConversation() {
    // server-side session is keyed by session_id — generate a new one to start fresh
    _sessionId = _makeSessionId()
}

// ─── Internal helpers ──────────────────────────────────────────────────────

// Split actions: navigation (handled here, executed immediately like switch_page / navigate_to_dashboard) vs dashboard (passed back to ChatWidget).
async function _processActions(actions) {
    const dashboardActions = []

    // navigate only if not already there — avoids double-navigate races within an action batch
    const _ensureOnPage = (path) => {
        if (!path || !_navigate) return
        if (window.location.pathname !== path) _navigate(path)
    }

    // ensure ArchiveControls is mounted (its registry is published on the archive route)
    const _ensureArchive = async () => {
        if (_archiveActions) return
        const conf = window.conf || {}
        _ensureOnPage(conf.ROUTES?.ARCHIVE?.path)
        await _poll(() => _archiveActions, 200, 10000) // eslint-disable-line no-loop-func
    }

    // ensure a mysite subsection is mounted: navigate to mysite, expand the panel, poll its registry
    const _ensureSubsection = async (section, getter) => {
        if (getter()) return
        const conf = window.conf || {}
        if (!_mysiteActions) {
            _ensureOnPage(conf.ROUTES?.MYSITE?.path)
            await _poll(() => _mysiteActions, 200, 10000) // eslint-disable-line no-loop-func
        }
        if (_mysiteActions) _mysiteActions.expandSection(section)
        await _poll(getter, 200, 10000) // eslint-disable-line no-loop-func
    }

    for (const act of actions) {
        // ─── existing navigation / page-level actions ──────────────────────────
        if (act.action === 'switch_page') {
            const conf = window.conf || {}
            _ensureOnPage(conf.ROUTES?.[_pageToRouteKey(act.page || 'home')]?.path)
        } else if (act.action === 'navigate_to_dashboard') {
            await navigateToDashboard()
        } else if (act.action === 'open_mysite_section') {
            const section = act.section
            const panelMap = { report: 'report', diary: 'diary', download: 'download', timelapse: 'timelapse', access: 'access', dashboard: 'dashboard' }
            const panel = section ? (panelMap[section] || section) : null
            if (!_mysiteActions) {
                const conf = window.conf || {}
                _ensureOnPage(conf.ROUTES?.MYSITE?.path)
                await _poll(() => _mysiteActions, 200, 10000) // eslint-disable-line no-loop-func
            }
            if (_mysiteActions && panel) _mysiteActions.expandSection(panel)
        } else if (act.action === 'switch_tab') {
            const tabMap = { erkennungen: 'detections', wetter: 'weather' }
            const tab = tabMap[act.tab] || act.tab
            if (!_dashboardActions) {
                await navigateToDashboard()
                await _poll(() => _dashboardActions, 200, 10000) // eslint-disable-line no-loop-func
            }
            if (_dashboardActions) _dashboardActions.switchTab(tab)
        } else if (act.action === 'set_dashboard_camera') {
            // dashboard's selectedCamera lives in DashboardAccordion; setter is plumbed through dashboardState.onSetSelectedCamera
            if (!_dashboardActions) {
                await navigateToDashboard()
                await _poll(() => _dashboardActions, 200, 10000) // eslint-disable-line no-loop-func
            }
            if (_dashboardActions && act.cam_id != null) _dashboardActions.setSelectedCameraId(act.cam_id)

        // ─── Archive ──────────────────────────────────────────────────────────
        } else if (act.action === 'set_archive_date') {
            await _ensureArchive()
            if (_archiveActions) {
                const dateStr = act.date || ''
                const timeStr = act.time || ''
                const dateTimeStr = timeStr ? `${dateStr} ${timeStr}` : `${dateStr} 07:00`
                _archiveActions.setDate(dateTimeStr)
            }
        } else if (act.action === 'archive_step_left') {
            await _ensureArchive()
            _archiveActions?.stepLeft?.()
        } else if (act.action === 'archive_step_right') {
            await _ensureArchive()
            _archiveActions?.stepRight?.()

        // ─── Report ───────────────────────────────────────────────────────────
        } else if (act.action === 'set_report_camera') {
            await _ensureSubsection('report', _getReportActions)
            if (_reportActions && act.cam_id != null) _reportActions.setSelectedCameraId(act.cam_id)
        } else if (act.action === 'open_report') {
            await _ensureSubsection('report', _getReportActions)
            _reportActions?.openDialog?.()

        // ─── Diary ────────────────────────────────────────────────────────────
        } else if (act.action === 'set_diary_camera') {
            await _ensureSubsection('diary', _getDiaryActions)
            if (_diaryActions && act.cam_id != null) _diaryActions.setSelectedCameraId(act.cam_id)
        } else if (act.action === 'set_diary_date') {
            // diary uses the shared archive timestamp under the hood, so this also moves the global date
            await _ensureSubsection('diary', _getDiaryActions)
            if (_diaryActions && act.date) _diaryActions.setDate(act.date)
        } else if (act.action === 'open_diary') {
            await _ensureSubsection('diary', _getDiaryActions)
            _diaryActions?.openDialog?.()

        // ─── Download ─────────────────────────────────────────────────────────
        } else if (act.action === 'set_download_camera') {
            await _ensureSubsection('download', _getDownloadActions)
            if (_downloadActions && act.cam_id != null) _downloadActions.setSelectedCameraId(act.cam_id)
        } else if (act.action === 'set_download_mode') {
            await _ensureSubsection('download', _getDownloadActions)
            if (_downloadActions && act.mode) _downloadActions.setMode(act.mode)
        } else if (act.action === 'set_download_dates') {
            await _ensureSubsection('download', _getDownloadActions)
            if (_downloadActions && act.start && act.end) _downloadActions.setDateRange(act.start, act.end)
        } else if (act.action === 'set_download_preset') {
            await _ensureSubsection('download', _getDownloadActions)
            if (_downloadActions && act.range) _downloadActions.setPredefinedRange(act.range)

        // ─── Timelapse ────────────────────────────────────────────────────────
        } else if (act.action === 'set_timelapse_camera') {
            await _ensureSubsection('timelapse', _getTimelapseActions)
            if (_timelapseActions && act.cam_id != null) _timelapseActions.setSelectedCameraId(act.cam_id)
        } else if (act.action === 'set_timelapse_mode') {
            await _ensureSubsection('timelapse', _getTimelapseActions)
            if (_timelapseActions && act.mode) _timelapseActions.setMode(act.mode)
        } else if (act.action === 'set_timelapse_dates') {
            await _ensureSubsection('timelapse', _getTimelapseActions)
            if (_timelapseActions && act.start && act.end) _timelapseActions.setDateRange(act.start, act.end)
        } else if (act.action === 'set_timelapse_preset') {
            await _ensureSubsection('timelapse', _getTimelapseActions)
            if (_timelapseActions && act.range) _timelapseActions.setPredefinedRange(act.range)
        } else if (act.action === 'set_timelapse_use_recommended_length') {
            await _ensureSubsection('timelapse', _getTimelapseActions)
            if (_timelapseActions) _timelapseActions.setUseRecommendedLength(act.use_recommended ?? act.value)
        } else if (act.action === 'set_timelapse_max_length') {
            await _ensureSubsection('timelapse', _getTimelapseActions)
            if (_timelapseActions && act.max_length_sec != null) _timelapseActions.setMaxLengthSec(act.max_length_sec)
        } else if (act.action === 'set_timelapse_resolution') {
            await _ensureSubsection('timelapse', _getTimelapseActions)
            // accepts {width, height} or 'original' / 'reduced' (registry resolves names against availableResolutions)
            if (_timelapseActions && act.resolution != null) _timelapseActions.setSelectedResolution(act.resolution)

        // ─── Access ───────────────────────────────────────────────────────────
        } else if (act.action === 'set_access_camera') {
            await _ensureSubsection('access', _getAccessActions)
            if (_accessActions && act.cam_id != null) _accessActions.setSelectedCameraId(act.cam_id)
        } else if (act.action === 'set_access_date') {
            await _ensureSubsection('access', _getAccessActions)
            if (_accessActions && act.date) _accessActions.setDate(act.date)
        } else if (act.action === 'set_access_vehicle_filter') {
            await _ensureSubsection('access', _getAccessActions)
            // accepts an array of vehicle ids, or null/[] to clear
            if (_accessActions) _accessActions.setVehicleFilter(Array.isArray(act.filter) ? act.filter : [])
        } else if (act.action === 'access_step_left') {
            await _ensureSubsection('access', _getAccessActions)
            _accessActions?.stepLeft?.()
        } else if (act.action === 'access_step_right') {
            await _ensureSubsection('access', _getAccessActions)
            _accessActions?.stepRight?.()

        // ─── unknown → defer to dashboard handler in ChatWidget ────────────────
        } else {
            dashboardActions.push(act)
        }
    }

    return { dashboardActions }
}

function _pageToRouteKey(page) {
    const map = {
        home: 'HOME', live: 'LIVE', timelapse: 'TIMELAPSE',
        archive: 'ARCHIVE', mysite: 'MYSITE', access: 'ACCESS',
    }
    return map[page] || 'HOME'
}

/** Derive current page name from window.location.pathname. */
function _getCurrentPage() {
    const path = window.location.pathname
    const conf = window.conf || {}
    const routes = conf.ROUTES || {}
    for (const [key, route] of Object.entries(routes)) {
        if (route.path === path) return key.toLowerCase()
    }
    return 'home'
}

function _poll(predicate, intervalMs, timeoutMs) {
    return new Promise(resolve => {
        let elapsed = 0
        const id = setInterval(() => {
            if (predicate()) { clearInterval(id); resolve(true) }
            elapsed += intervalMs
            if (elapsed >= timeoutMs) { clearInterval(id); resolve(false) }
        }, intervalMs)
    })
}

// ─── Mock Mode ─────────────────────────────────────────────────────────────

const MOCK_RESPONSES = [
    'Das Projekt begann am 5. November 2025. Der Rohbau ist seit Ende Januar 2026 weitgehend fertiggestellt.',
    'Aktuell sind auf der Baustelle diverse Geräte im Einsatz, darunter Bagger, Radlader und LKW.',
    'Ich bin der Baustellenassistent. Ich kann dir Informationen zum Baufortschritt, Erkennungsdaten und Wetter geben.',
    'Du kannst mich nach Fahrzeugerkennungen, Wetterdaten oder Projektphasen fragen.',
]
let _mockCounter = 0

function _handleMock(userMessage) {
    const reply = MOCK_RESPONSES[_mockCounter % MOCK_RESPONSES.length]
    _mockCounter++
    return { reply, mock: true }
}
